import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Zap, Shield, Users, Thermometer, Truck, Calculator, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProjectInquiryPopup from '../components/ProjectInquiryPopup';

export default function ComparisonToolPage() {
  const [quantity, setQuantity] = useState(100);
  const [steelLength, setSteelLength] = useState(20);
  const [laborCost, setLaborCost] = useState(35);
  const [freightRate, setFreightRate] = useState(2);
  const [showPopup, setShowPopup] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  // Show popup after 30 seconds if user is still on page
  useEffect(() => {
    // Check if popup was already shown in this session
    const alreadyShown = sessionStorage.getItem('comparison_popup_shown');
    if (alreadyShown) {
      setHasShownPopup(true);
      return;
    }

    const timer = setTimeout(() => {
      if (!hasShownPopup) {
        setShowPopup(true);
        setHasShownPopup(true);
        sessionStorage.setItem('comparison_popup_shown', 'true');
      }
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [hasShownPopup]);

  // Material specs
  const bfrp = {
    tensileStrength: 275, // MPa
    weight: 1.8, // kg/meter for rebar equivalent
    costPerMeter: 18,
    thermalConduct: 0, // W/m·K
    electricalConduct: 0, // S/m
    corrosionRate: 0,
    liftCapacity: 2, // workers needed per bundle of 20
  };

  const steel = {
    tensileStrength: 90, // Grade 60 MPa
    weight: 7.2, // kg/meter
    costPerMeter: 6,
    thermalConduct: 50, // W/m·K
    electricalConduct: 1e7, // S/m
    corrosionRate: 0.15, // mm/year
    liftCapacity: 6, // workers needed per bundle of 20
  };

  // Calculations
  const totalLength = quantity * steelLength;
  const bfrpWeight = totalLength * bfrp.weight;
  const steelWeight = totalLength * steel.weight;
  const weightRatio = ((steelWeight - bfrpWeight) / steelWeight * 100).toFixed(1);

  const bfrpMaterialCost = totalLength * bfrp.costPerMeter;
  const steelMaterialCost = totalLength * steel.costPerMeter;

  const bfrpFreight = (bfrpWeight * freightRate) / 1000;
  const steelFreight = (steelWeight * freightRate) / 1000;

  const bfrpLaborBundles = Math.ceil(quantity / 20);
  const steelLaborBundles = Math.ceil(quantity / 20);
  const bfrpLaborCost = bfrpLaborBundles * bfrp.liftCapacity * laborCost * 0.5;
  const steelLaborCost = steelLaborBundles * steel.liftCapacity * laborCost * 0.5;

  const steelCorrosionYears = 20;
  const steelLossMMTotal = steelCorrosionYears * steel.corrosionRate;
  const steelLossPercent = (steelLossMMTotal / 12.7) * 100; // Rebar diameter ~12.7mm
  const replacementCost = (steelLossPercent / 100) * steelMaterialCost * 1.5; // 50% labor premium

  const bfrpTotal = bfrpMaterialCost + bfrpFreight + bfrpLaborCost;
  const steelTotal = steelMaterialCost + steelFreight + steelLaborCost + replacementCost;
  const roi = (((steelTotal - bfrpTotal) / steelTotal) * 100).toFixed(1);
  const totalSavings = steelTotal - bfrpTotal;

  // Calculator data to pass to popup
  const calculatorData = {
    quantity,
    length: steelLength,
    laborCost,
    freightRate,
    savings: Math.round(totalSavings),
    roi: parseFloat(roi)
  };

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="comparison-tool-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <Calculator size={24} className="text-gold" />
              <span className="tech-label">//MATERIALS_ANALYSIS</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              BFRP vs <span className="text-gold">Steel</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Basalt Fiber Reinforced Polymer Comparison Tool. Calculate real-world 
              lifecycle costs and see why composites outperform steel.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Input Controls */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            <div className="border border-border-dark p-6 bg-surface-dark/30">
              <label className="block text-mono text-xs text-gold/70 uppercase tracking-wider mb-3">
                Quantity (pieces)
              </label>
              <input
                type="range"
                min="10"
                max="1000"
                step="10"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full h-1 bg-border-dark rounded appearance-none cursor-pointer accent-gold"
                data-testid="quantity-slider"
              />
              <div className="heading-display text-3xl text-gold mt-2">{quantity}</div>
            </div>

            <div className="border border-border-dark p-6 bg-surface-dark/30">
              <label className="block text-mono text-xs text-gold/70 uppercase tracking-wider mb-3">
                Length per piece (meters)
              </label>
              <input
                type="number"
                min="1"
                max="50"
                step="1"
                value={steelLength}
                onChange={(e) => setSteelLength(Number(e.target.value))}
                className="input text-xl font-mono"
                data-testid="length-input"
              />
            </div>

            <div className="border border-border-dark p-6 bg-surface-dark/30">
              <label className="block text-mono text-xs text-gold/70 uppercase tracking-wider mb-3">
                Labor cost ($/hour)
              </label>
              <input
                type="number"
                min="15"
                max="100"
                step="5"
                value={laborCost}
                onChange={(e) => setLaborCost(Number(e.target.value))}
                className="input text-xl font-mono"
                data-testid="labor-input"
              />
            </div>

            <div className="border border-border-dark p-6 bg-surface-dark/30">
              <label className="block text-mono text-xs text-gold/70 uppercase tracking-wider mb-3">
                Freight rate ($/kg)
              </label>
              <input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                value={freightRate}
                onChange={(e) => setFreightRate(Number(e.target.value))}
                className="input text-xl font-mono"
                data-testid="freight-input"
              />
            </div>
          </motion.div>

          {/* ROI Banner */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/30 p-8 mb-12"
            data-testid="roi-banner"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-mono text-xs text-gold/70 uppercase tracking-wider mb-2">Total Lifecycle Savings</p>
                <p className="heading-display text-5xl text-gold">${(steelTotal - bfrpTotal).toFixed(0)}</p>
              </div>
              <div className="text-right">
                <p className="text-mono text-xs text-gold/70 uppercase tracking-wider mb-2">ROI</p>
                <p className="heading-display text-4xl text-stone">{roi}%</p>
              </div>
            </div>
          </motion.div>

          {/* Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Tensile Strength */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Tensile Strength</h2>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone/60">BFRP</span>
                    <span className="heading-display text-2xl text-gold">{bfrp.tensileStrength} MPa</span>
                  </div>
                  <div className="w-full bg-border-dark h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-gold to-gold/70 h-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone/60">Grade 60 Steel</span>
                    <span className="heading-display text-2xl text-stone/50">{steel.tensileStrength} MPa</span>
                  </div>
                  <div className="w-full bg-border-dark h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-stone/40 to-stone/20 h-full" style={{ width: `${(steel.tensileStrength / bfrp.tensileStrength) * 100}%` }}></div>
                  </div>
                </div>
                <div className="pt-4 border-t border-border-dark">
                  <p className="text-gold text-mono text-sm font-medium">BFRP is {(bfrp.tensileStrength / steel.tensileStrength).toFixed(1)}× stronger</p>
                </div>
              </div>
            </motion.div>

            {/* Weight */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <Truck className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Total Weight</h2>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone/60">BFRP</span>
                    <span className="heading-display text-2xl text-gold">{bfrpWeight.toFixed(0)} kg</span>
                  </div>
                  <div className="w-full bg-border-dark h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-gold to-gold/70 h-full" style={{ width: `${(bfrpWeight / steelWeight) * 100}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone/60">Grade 60 Steel</span>
                    <span className="heading-display text-2xl text-stone/50">{steelWeight.toFixed(0)} kg</span>
                  </div>
                  <div className="w-full bg-border-dark h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-stone/40 to-stone/20 h-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div className="pt-4 border-t border-border-dark">
                  <p className="text-gold text-mono text-sm font-medium">BFRP weighs {weightRatio}% less</p>
                  <p className="text-stone/40 text-sm mt-2">Freight savings: ${(steelFreight - bfrpFreight).toFixed(0)}</p>
                </div>
              </div>
            </motion.div>

            {/* Corrosion & Lifecycle */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Corrosion (20-Year Lifecycle)</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-charcoal/50 p-4 border-l-2 border-red-500/70">
                  <p className="text-stone/60 text-sm mb-2">Grade 60 Steel</p>
                  <p className="heading-section text-lg text-red-500/80">{steelLossMMTotal.toFixed(2)} mm loss (~{steelLossPercent.toFixed(0)}%)</p>
                  <p className="text-stone/40 text-xs mt-2">Requires replacement at estimated cost:</p>
                  <p className="heading-section text-lg text-red-500/70 mt-1">${replacementCost.toFixed(0)}</p>
                </div>
                <div className="bg-charcoal/50 p-4 border-l-2 border-gold">
                  <p className="text-stone/60 text-sm mb-2">BFRP</p>
                  <p className="heading-section text-lg text-gold">Zero degradation</p>
                  <p className="text-stone/40 text-xs mt-2">No replacement cost</p>
                </div>
              </div>
            </motion.div>

            {/* Handling & Labor */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Handling (per bundle of 20)</h2>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <span className="text-stone/60">BFRP</span>
                    <span className="heading-display text-2xl text-gold">{bfrp.liftCapacity} workers</span>
                  </div>
                  <div className="flex gap-2">
                    {[...Array(bfrp.liftCapacity)].map((_, i) => (
                      <div key={i} className="w-8 h-8 bg-gold/20 border border-gold/50 rounded-full flex items-center justify-center">
                        <span className="text-xs text-gold">●</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <span className="text-stone/60">Grade 60 Steel</span>
                    <span className="heading-display text-2xl text-stone/50">{steel.liftCapacity} workers</span>
                  </div>
                  <div className="flex gap-2">
                    {[...Array(steel.liftCapacity)].map((_, i) => (
                      <div key={i} className="w-8 h-8 bg-stone/10 border border-stone/30 rounded-full flex items-center justify-center">
                        <span className="text-xs text-stone/40">●</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-t border-border-dark">
                  <p className="text-gold text-mono text-sm font-medium">{steel.liftCapacity - bfrp.liftCapacity}× fewer workers needed</p>
                  <p className="text-stone/40 text-sm mt-2">Labor savings: ${(steelLaborCost - bfrpLaborCost).toFixed(0)}</p>
                </div>
              </div>
            </motion.div>

            {/* Conductivity */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Electrical Conductivity</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-charcoal/50 p-4 border-l-2 border-gold">
                  <p className="text-stone/60 text-sm mb-2">BFRP</p>
                  <p className="heading-section text-lg text-gold">Zero conductivity</p>
                  <p className="text-stone/40 text-xs mt-2">
                    ✓ Safe for MRI rooms<br />
                    ✓ Ideal for toll booths<br />
                    ✓ Non-conductive enclosures
                  </p>
                </div>
                <div className="bg-charcoal/50 p-4 border-l-2 border-stone/30">
                  <p className="text-stone/60 text-sm mb-2">Grade 60 Steel</p>
                  <p className="heading-section text-lg text-stone/50">10⁷ S/m (Conductive)</p>
                  <p className="text-stone/40 text-xs mt-2">
                    ✗ Electrical grounding risk<br />
                    ✗ MRI incompatible<br />
                    ✗ EMI shielding required
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Thermal Conductivity */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="border border-border-dark p-8 bg-surface-dark/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <Thermometer className="w-5 h-5 text-gold" />
                <h2 className="heading-section text-xl text-stone">Thermal Conductivity</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-charcoal/50 p-4 border-l-2 border-gold">
                  <p className="text-stone/60 text-sm mb-2">BFRP</p>
                  <p className="heading-section text-lg text-gold">~0 W/m·K</p>
                  <p className="text-stone/40 text-xs mt-2">
                    ✓ Insulated by design<br />
                    ✓ Thermal breaks in precast<br />
                    ✓ Energy efficient panels
                  </p>
                </div>
                <div className="bg-charcoal/50 p-4 border-l-2 border-red-500/50">
                  <p className="text-stone/60 text-sm mb-2">Grade 60 Steel</p>
                  <p className="heading-section text-lg text-red-500/70">~50 W/m·K</p>
                  <p className="text-stone/40 text-xs mt-2">
                    ✗ Thermal bridges<br />
                    ✗ Heat transfer<br />
                    ✗ Requires additional insulation
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Cost Breakdown */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="border border-border-dark p-8 bg-surface-dark/30 mb-8"
          >
            <h2 className="heading-section text-2xl text-stone mb-8">Cost Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gold mb-6">BFRP Total: <span className="font-mono">${bfrpTotal.toFixed(0)}</span></h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Material</span>
                    <span className="text-stone font-mono font-bold">${bfrpMaterialCost.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Freight</span>
                    <span className="text-stone font-mono font-bold">${bfrpFreight.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Labor (handling)</span>
                    <span className="text-stone font-mono font-bold">${bfrpLaborCost.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Replacement</span>
                    <span className="text-stone font-mono font-bold">$0</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-stone/60 mb-6">Steel Total: <span className="font-mono">${steelTotal.toFixed(0)}</span></h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Material</span>
                    <span className="text-stone font-mono font-bold">${steelMaterialCost.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Freight</span>
                    <span className="text-stone font-mono font-bold">${steelFreight.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50">
                    <span className="text-stone/60">Labor (handling)</span>
                    <span className="text-stone font-mono font-bold">${steelLaborCost.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-charcoal/50 border-l-2 border-red-500/50">
                    <span className="text-stone/60">20-year replacement</span>
                    <span className="text-red-500/70 font-mono font-bold">${replacementCost.toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Footer note */}
          <div className="text-center text-stone/30 text-xs border-t border-border-dark pt-8">
            <p className="font-mono">Data based on standard BFRP rebar (12.7mm equiv.) vs. Grade 60 steel rebar</p>
            <p className="mt-2">All costs and properties are approximations for estimation purposes</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 border-t border-border-dark bg-surface-dark/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="heading-display text-stone text-3xl mb-4">Ready to Specify?</h2>
          <p className="text-stone/50 mb-8">
            Get a formal quote tailored to your project requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn btn-primary">
              Request Quote
              <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/samples" className="btn btn-outline">
              Request Samples
            </Link>
          </div>
        </div>
      </section>

      {/* Project Inquiry Popup */}
      <ProjectInquiryPopup 
        isVisible={showPopup}
        onClose={() => setShowPopup(false)}
        calculatorData={calculatorData}
      />
    </div>
  );
}
